import React, { createContext, useContext } from 'react';

const ThemeContext = createContext(null);

// DARK THEME — matches the current design
const darkTheme = {
  name: 'dark',
  bgGradient: 'radial-gradient(ellipse at top, #1e1b4b 0%, #0a0e1a 50%)',
  bgMain: '#0a0e1a',
  bgCard: 'rgba(17, 24, 39, 0.6)',
  bgCardSolid: 'rgba(17, 24, 39, 0.95)',
  bgNested: 'rgba(15, 23, 42, 0.6)',
  borderSubtle: 'rgba(255, 255, 255, 0.08)',
  borderHover: 'rgba(6, 182, 212, 0.3)',
  textPrimary: '#f8fafc',
  textSecondary: '#94a3b8',
  textTertiary: '#64748b',
  cyan: '#06b6d4',
  cyanBright: '#22d3ee',
  violet: '#8b5cf6',
  violetBright: '#a78bfa',
  green: '#10b981',
  greenBright: '#34d399',
  orange: '#f59e0b',
  orangeBright: '#fbbf24',
  red: '#ef4444',
  redBright: '#fca5a5',
  inputBg: 'rgba(15, 23, 42, 0.6)',
  dropdownBg: 'rgba(17, 24, 39, 0.98)',
  optionBg: '#1f2937',
  tableHeaderBg: 'rgba(6, 182, 212, 0.05)',
  tableRowAlt: 'rgba(255, 255, 255, 0.02)',
  tableRowHover: 'rgba(6, 182, 212, 0.05)',
  shadowCard: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 60px rgba(6, 182, 212, 0.1)',
};

// LIGHT THEME — same structure, lighter palette
const lightTheme = {
  name: 'light',
  bgGradient: 'linear-gradient(180deg, #f0f4f8 0%, #e8edf4 100%)',
  bgMain: '#f0f4f8',
  bgCard: 'rgba(255, 255, 255, 0.85)',
  bgCardSolid: 'rgba(255, 255, 255, 0.98)',
  bgNested: 'rgba(241, 245, 249, 0.8)',
  borderSubtle: 'rgba(0, 0, 0, 0.08)',
  borderHover: 'rgba(6, 182, 212, 0.4)',
  textPrimary: '#0f172a',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
  cyan: '#0891b2',
  cyanBright: '#0e7490',
  violet: '#7c3aed',
  violetBright: '#6d28d9',
  green: '#059669',
  greenBright: '#047857',
  orange: '#d97706',
  orangeBright: '#b45309',
  red: '#dc2626',
  redBright: '#b91c1c',
  inputBg: 'rgba(255, 255, 255, 0.9)',
  dropdownBg: 'rgba(255, 255, 255, 0.98)',
  optionBg: '#ffffff',
  tableHeaderBg: 'rgba(6, 182, 212, 0.08)',
  tableRowAlt: 'rgba(0, 0, 0, 0.02)',
  tableRowHover: 'rgba(6, 182, 212, 0.06)',
  shadowCard: '0 10px 30px -10px rgba(0, 0, 0, 0.15)',
};

export function ThemeProvider({ children }) {
  // Theme is permanently forced to light. Dark mode has been retired; the
  // darkTheme object above is left defined for easy re-enable later.
  // toggleTheme is kept as a no-op so existing consumers that destructure it
  // (Sidebar, Login, Dashboard) don't break.
  const themeName = 'light';
  const theme = lightTheme;
  const toggleTheme = () => {};

  return (
    <ThemeContext.Provider value={{ theme, themeName, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
